/**
 * **C09 · The 1099 letter and the W-9 chase letter** (Stream C, Documents.dc.html): two half-letter
 * letters, one per payee, signed by the finance director by name — never by CIDER. In January, the
 * 1099-NEC to each payee Money's 1099 watch says a form is due for (`payables.ten99Watch`, the
 * year's threshold); in December, the chase for a W-9 before a check that is ready can go
 * (Bills to pay's rows for a 1099 vendor with no W-9 on file). The figures are Money's; this
 * writes the letters around them.
 */
import { dollars } from './dollars.js';
import { NO_DATA, type PaperPage } from './paper_page.js';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** `2027-01-24` → `Jan 24, 2027`. */
export const letterDay = (ymd: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  return m ? `${MONTHS[Number(m[2]) - 1] ?? ''} ${String(Number(m[3]))}, ${m[1] ?? ''}` : NO_DATA;
};

/** Who signs: the finance director by name, or a line a hand signs when Roles names nobody. */
const signer = (name: string | null): string[] => [`${name ?? ''} · finance director`, ''];

export interface Ten99Payee {
  payee: string;
  /** The vendor's contact, by name (`c/o Mark Nowak`); null when none is on the vendor. */
  contact: string | null;
  paidCents: number;
}

export function ten99Letters(i: {
  year: number;
  day: string;
  payees: Ten99Payee[] | null;
  financeDirector: string | null;
}): PaperPage[] {
  const who = i.financeDirector ?? 'the finance director';
  const year = String(i.year);
  // No letter to write is said on a page, never an empty print.
  const none = (to: string, says: string): PaperPage[] => [
    {
      title: `1099-NEC enclosed · ${year}`,
      sub: `${to} · ${letterDay(i.day)}`,
      blocks: [{ head: '', cols: '1fr', rows: [[says]] }],
      sign: signer(i.financeDirector),
      foot: '1099 letter · one per payee over the threshold',
    },
  ];
  if (!i.payees) return none(`To: ${NO_DATA}`, `${NO_DATA} — the 1099 watch did not answer.`);
  if (!i.payees.length)
    return none('No letter to send', `No payee was paid over the threshold in ${year}.`);
  return i.payees.map((p) => ({
    title: `1099-NEC enclosed · ${year}`,
    sub: `To: ${p.payee}${p.contact ? ` · c/o ${p.contact}` : ''} · ${letterDay(i.day)}`,
    blocks: [
      {
        head: '',
        cols: '1fr',
        rows: [
          [
            `Enclosed is your Form 1099-NEC for payments from Saint Lawrence Apple Fest in ${year}: ${dollars(p.paidCents)}. A copy has been filed with the IRS. If the amount or your details are wrong, call ${who}, finance director, before Feb 15.`,
          ],
        ],
      },
    ],
    sign: signer(i.financeDirector),
    foot: '1099 letter · one per payee over the threshold',
  }));
}

export interface W9Chase {
  payee: string;
  cents: number;
  /** What the money is for, in the bills' own words. */
  what: string;
  /** The paper it is owed on (`PO 2027-051`). */
  paper: string;
}

export function w9ChaseLetters(i: {
  day: string;
  owed: W9Chase[] | null;
  financeDirector: string | null;
}): PaperPage[] {
  const letter = (to: string, body: string): PaperPage => ({
    title: 'We need your W-9',
    sub: `${to} · ${letterDay(i.day)}`,
    blocks: [{ head: '', cols: '1fr', rows: [[body]] }],
    sign: signer(i.financeDirector),
    foot: 'W-9 chase · the check waits',
  });
  if (!i.owed) return [letter(`To: ${NO_DATA}`, `${NO_DATA} — Bills to pay did not answer.`)];
  if (!i.owed.length) return [letter('No letter to send', 'No check is waiting on a W-9.')];
  return i.owed.map((o) =>
    letter(
      `To: ${o.payee}`,
      `We owe you ${dollars(o.cents)} for ${o.what} (${o.paper}). The check is ready. We cannot release it without a Form W-9 on file — please return the enclosed form, and the check goes out the same week.`,
    ),
  );
}
